import { useEffect, useState } from 'react'
import { Button, Popup } from '@healingpaper/cell'

import { generateTagId } from '@/utils/test-automation'

export interface ConfirmPopupProps {
  text: {
    title: string
    description: string
    confirm: string
    cancel: string
  }
  isVisible: boolean
  onClickBackground?: () => void
  onCancel?: () => void
  onConfirm: () => Promise<void> | void
  options?: {
    disableCloseOnDimClick?: boolean
  }
}

const ConfirmPopup = ({
  text,
  isVisible: isVisibleProps,
  onClickBackground,
  onCancel,
  onConfirm,
  options,
}: ConfirmPopupProps): JSX.Element => {
  const { isVisible, closePopup } = useSyncPopupState(isVisibleProps)

  const handleClickConfirm = () => {
    onConfirm()
    closePopup()
  }

  const handleClickCancel = () => {
    onCancel?.()
    closePopup()
  }

  const handleClickBackground = () => {
    onClickBackground?.()
    if (!options?.disableCloseOnDimClick) {
      closePopup()
    }
  }

  if (!isVisible) {
    return <></>
  }

  return (
    <Popup escapable={true} width={'260px'} onClose={handleClickBackground}>
      <h3 id={generateTagId('all', 'popup', 'text', 'title')} className="m-0 whitespace-pre-wrap typo-title3">
        {text.title}
      </h3>
      <p
        id={generateTagId('all', 'popup', 'text', 'description')}
        className="m-0 mt-2 whitespace-pre-wrap typo-description2 text-gray-800"
      >
        {text.description}
      </p>

      <div className="mt-6 flex justify-end gap-2">
        <Button
          id={generateTagId('all', 'popup', 'btn', 'cancel')}
          size="Medium"
          hierarchy="Tertiary"
          onClick={handleClickCancel}
        >
          {text.cancel}
        </Button>
        <Button id={generateTagId('all', 'popup', 'btn', 'confirm')} size="Medium" onClick={handleClickConfirm}>
          {text.confirm}
        </Button>
      </div>
    </Popup>
  )
}

const useSyncPopupState = (isVisibleProps: boolean) => {
  const [isVisible, setIsVisible] = useState(isVisibleProps)

  useEffect(() => {
    setIsVisible(isVisibleProps)
  }, [isVisibleProps])

  return { isVisible, closePopup: () => setIsVisible(false), openPopup: () => setIsVisible(true) }
}

export default ConfirmPopup
